import { isEqual } from 'lodash-es';
import { useRef } from 'react';

import type { FilterFieldProps } from '../FilterField.js';
import type { FilterFieldTree } from '../types/tree-nodes.js';
import type { FilterFieldValidatorMap } from '../types/validation.js';
import { convertLezerTree } from '../utils/conversion/tree-conversion.js';

/** @internal */
export function useCachedTreeConversion() {
  const prevDoc = useRef<string | undefined>(undefined);
  const prevSyntax = useRef<FilterFieldProps['syntax'] | null>(null);
  const cachedTree = useRef<FilterFieldTree | undefined>(undefined);
  const cachedIsValid = useRef(true);
  const prevValidatorMap = useRef<FilterFieldValidatorMap>(undefined);

  function getSyntaxTree(
    args: Parameters<typeof convertLezerTree>[0],
  ): ReturnType<typeof convertLezerTree> {
    const { doc, syntax, validatorMap } = args;

    // If the document and syntax mode didn't change and we already have a converted tree, we can return the cached tree.
    if (
      prevDoc.current === doc?.toString() &&
      prevSyntax.current === syntax &&
      isEqual(validatorMap, prevValidatorMap.current) &&
      cachedTree.current
    ) {
      return { tree: cachedTree.current, isValid: cachedIsValid.current };
    }

    prevDoc.current = doc?.toString();
    prevSyntax.current = syntax;
    prevValidatorMap.current = validatorMap;
    const filter = convertLezerTree(args);
    cachedTree.current = filter.tree;
    cachedIsValid.current = filter.isValid;

    return filter;
  }

  return { getSyntaxTree };
}
